import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  Minus, Plus, AlignLeft, AlignCenter, AlignRight,
  Bold, Italic, Underline, CaseSensitive, Type,
  Sparkles, ChevronDown, Baseline
} from 'lucide-react';

const FONT_OPTIONS = [
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Oswald', label: 'Oswald' },
  { value: 'Bebas Neue', label: 'Bebas Neue' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Raleway', label: 'Raleway' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Inter', label: 'Inter' },
  { value: 'DM Sans', label: 'DM Sans' },
  { value: 'Space Grotesk', label: 'Space Grotesk' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
];

const Separator = () => <div className="w-px h-6 bg-slate-200 mx-1 shrink-0" />;

const ToolBtn = ({ active, onClick, title, children, testId }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors text-sm
      ${active ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
  >
    {children}
  </button>
);

/* ── Popover wrapper (portal-based, escapes overflow) ── */
const Popover = ({ trigger, children, align = 'center', width = 'auto' }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let left = rect.left;
    if (align === 'center') left = rect.left + rect.width / 2;
    else if (align === 'right') left = rect.right;
    setPos({ top: rect.bottom + 6, left });
  }, [align]);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const close = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', updatePos);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', updatePos);
    };
  }, [open, updatePos]);

  const transformStyle = align === 'center' ? 'translateX(-50%)'
    : align === 'right' ? 'translateX(-100%)' : 'none';

  return (
    <div ref={triggerRef} className="relative shrink-0">
      <div onClick={() => { if (!open) updatePos(); setOpen(!open); }}>{trigger}</div>
      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-slate-200 p-3"
          style={{ top: pos.top, left: pos.left, transform: transformStyle, minWidth: width === 'auto' ? undefined : width }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

const CanvaTextToolbar = ({ layer, onUpdate }) => {
  if (!layer || layer.type !== 'text') return null;

  const u = (field, value) => onUpdate(layer.id, { [field]: value });

  const fontSize = layer.fontSize || 48;
  const fontFamily = layer.fontFamily || 'Montserrat';
  const fontColor = layer.fontColor?.startsWith('#') ? layer.fontColor : '#ffffff';
  const textAlign = layer.textAlign || 'center';
  const isBold = layer.fontWeight === 'bold';
  const isItalic = layer.fontStyle === 'italic';
  const isUnderline = layer.textDecoration === 'underline';
  const isUppercase = layer.textTransform === 'uppercase';
  const lineHeight = layer.lineHeight || 1.2;
  const opacity = layer.opacity ?? 1;
  const shadow = layer.shadow || {};
  const bgHighlight = layer.bgHighlight || {};
  const hasShadow = !!shadow.color;
  const hasBg = bgHighlight.enabled;

  return (
    <div
      className="flex items-center gap-0.5 px-3 py-1.5 bg-white border-b border-slate-200 flex-wrap"
      data-testid="canva-text-toolbar"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* ── Font Family Dropdown ── */}
      <Popover
        align="left"
        trigger={
          <button className="h-8 px-3 flex items-center gap-1.5 rounded-md hover:bg-slate-50 border border-slate-200 text-sm min-w-[140px] max-w-[180px] transition-colors"
            data-testid="ft-font-family-btn">
            <span className="truncate text-slate-700" style={{ fontFamily }}>{fontFamily}</span>
            <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" />
          </button>
        }
      >
        <div className="w-52 max-h-64 overflow-y-auto" data-testid="ft-font-family-list">
          {FONT_OPTIONS.map(f => (
            <button key={f.value}
              className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors
                ${fontFamily === f.value ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              style={{ fontFamily: f.value }}
              onClick={() => u('fontFamily', f.value)}
              data-testid={`ft-font-${f.value.replace(/\s/g, '-')}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Popover>

      <Separator />

      {/* ── Font Size ── */}
      <div className="flex items-center gap-0" data-testid="ft-font-size-group">
        <button className="h-8 w-8 flex items-center justify-center rounded-l-md border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"
          onClick={() => u('fontSize', Math.max(8, fontSize - 2))} data-testid="ft-size-down">
          <Minus className="h-3.5 w-3.5" />
        </button>
        <input
          type="number"
          value={Math.round(fontSize)}
          onChange={(e) => { const v = Number(e.target.value); if (v >= 8 && v <= 400) u('fontSize', v); }}
          className="h-8 w-12 text-center text-sm border-y border-slate-200 bg-white text-slate-700 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          data-testid="ft-size-input"
        />
        <button className="h-8 w-8 flex items-center justify-center rounded-r-md border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"
          onClick={() => u('fontSize', Math.min(400, fontSize + 2))} data-testid="ft-size-up">
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <Separator />

      {/* ── Text Color ── */}
      <div className="relative h-8 w-8 flex items-center justify-center" data-testid="ft-color-wrap">
        <div className="flex flex-col items-center justify-center gap-0.5">
          <span className="text-sm font-bold text-slate-700 leading-none">A</span>
          <div className="w-5 h-1 rounded-full" style={{ background: `linear-gradient(90deg, ${fontColor}, ${fontColor})` }} />
        </div>
        <input type="color" value={fontColor}
          onChange={(e) => u('fontColor', e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          data-testid="ft-font-color" />
      </div>

      <Separator />

      {/* ── Bold / Italic / Underline / Strikethrough ── */}
      <ToolBtn active={isBold} onClick={() => u('fontWeight', isBold ? 'normal' : 'bold')} title="Bold" testId="ft-bold">
        <Bold className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={isItalic} onClick={() => u('fontStyle', isItalic ? 'normal' : 'italic')} title="Italic" testId="ft-italic">
        <Italic className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={isUnderline} onClick={() => u('textDecoration', isUnderline ? 'none' : 'underline')} title="Underline" testId="ft-underline">
        <Underline className="h-4 w-4" />
      </ToolBtn>

      <Separator />

      {/* ── Uppercase Toggle ── */}
      <ToolBtn active={isUppercase} onClick={() => u('textTransform', isUppercase ? 'none' : 'uppercase')} title="Uppercase" testId="ft-uppercase">
        <span className="text-xs font-semibold">aA</span>
      </ToolBtn>

      <Separator />

      {/* ── Alignment ── */}
      <ToolBtn active={textAlign === 'left'} onClick={() => u('textAlign', 'left')} title="Align Left" testId="ft-align-left">
        <AlignLeft className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={textAlign === 'center'} onClick={() => u('textAlign', 'center')} title="Align Center" testId="ft-align-center">
        <AlignCenter className="h-4 w-4" />
      </ToolBtn>
      <ToolBtn active={textAlign === 'right'} onClick={() => u('textAlign', 'right')} title="Align Right" testId="ft-align-right">
        <AlignRight className="h-4 w-4" />
      </ToolBtn>

      <Separator />

      {/* ── Line Height / Spacing ── */}
      <Popover
        trigger={
          <button className="h-8 w-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-50 transition-colors"
            title="Line Spacing" data-testid="ft-spacing-btn">
            <Baseline className="h-4 w-4" />
          </button>
        }
      >
        <div className="w-48" data-testid="ft-spacing-popover">
          <p className="text-xs font-medium text-slate-500 mb-2">Line Spacing: {lineHeight.toFixed(1)}</p>
          <Slider value={[lineHeight * 10]} onValueChange={([val]) => u('lineHeight', val / 10)} min={8} max={30} step={1} />
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-slate-400">0.8</span>
            <span className="text-[10px] text-slate-400">3.0</span>
          </div>
        </div>
      </Popover>

      <Separator />

      {/* ── Opacity ── */}
      <Popover
        trigger={
          <button className="h-8 px-2 flex items-center gap-1 rounded-md text-slate-600 hover:bg-slate-50 transition-colors text-xs"
            title="Opacity" data-testid="ft-opacity-btn">
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="6" height="6" fill="currentColor" opacity="1" />
              <rect x="9" y="1" width="6" height="6" fill="currentColor" opacity="0.5" />
              <rect x="1" y="9" width="6" height="6" fill="currentColor" opacity="0.25" />
              <rect x="9" y="9" width="6" height="6" fill="currentColor" opacity="0.1" />
            </svg>
          </button>
        }
      >
        <div className="w-48" data-testid="ft-opacity-popover">
          <p className="text-xs font-medium text-slate-500 mb-2">Opacity: {Math.round(opacity * 100)}%</p>
          <Slider value={[opacity * 100]} onValueChange={([val]) => u('opacity', val / 100)} max={100} step={1} />
        </div>
      </Popover>

      <Separator />

      {/* ── Effects (Shadow + Background) ── */}
      <Popover
        align="right"
        trigger={
          <button className={`h-8 px-3 flex items-center gap-1.5 rounded-md text-sm transition-colors
            ${(hasShadow || hasBg) ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'text-slate-600 hover:bg-slate-50 border border-transparent'}`}
            data-testid="ft-effects-btn">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Effects</span>
          </button>
        }
      >
        <div className="w-64 space-y-4" data-testid="ft-effects-popover">
          {/* Shadow */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-700">Text Shadow</span>
              <button
                className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${hasShadow ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}
                onClick={() => {
                  if (hasShadow) u('shadow', {});
                  else u('shadow', { color: '#000000', blur: 4, offsetX: 2, offsetY: 2 });
                }}
                data-testid="ft-toggle-shadow"
              >
                {hasShadow ? 'ON' : 'OFF'}
              </button>
            </div>
            {hasShadow && (
              <div className="space-y-2 pl-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 w-10">Color</span>
                  <input type="color" value={shadow.color || '#000000'}
                    onChange={(e) => u('shadow', { ...shadow, color: e.target.value })}
                    className="h-6 w-10 rounded cursor-pointer" data-testid="ft-shadow-color" />
                </div>
                <div>
                  <span className="text-[10px] text-slate-500">Blur: {shadow.blur || 0}px</span>
                  <Slider value={[shadow.blur || 0]} onValueChange={([val]) => u('shadow', { ...shadow, blur: val })} max={20} step={1} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[10px] text-slate-500">X: {shadow.offsetX || 0}</span>
                    <Slider value={[shadow.offsetX || 0]} onValueChange={([val]) => u('shadow', { ...shadow, offsetX: val })} min={-20} max={20} step={1} />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500">Y: {shadow.offsetY || 0}</span>
                    <Slider value={[shadow.offsetY || 0]} onValueChange={([val]) => u('shadow', { ...shadow, offsetY: val })} min={-20} max={20} step={1} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Background Highlight */}
          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-700">Background Box</span>
              <button
                className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${hasBg ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}
                onClick={() => u('bgHighlight', { ...bgHighlight, enabled: !hasBg, color: bgHighlight.color || '#000000', opacity: bgHighlight.opacity ?? 0.6, padding: bgHighlight.padding || 12, borderRadius: bgHighlight.borderRadius || 4 })}
                data-testid="ft-toggle-bg"
              >
                {hasBg ? 'ON' : 'OFF'}
              </button>
            </div>
            {hasBg && (
              <div className="space-y-2 pl-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 w-10">Color</span>
                  <input type="color" value={bgHighlight.color || '#000000'}
                    onChange={(e) => u('bgHighlight', { ...bgHighlight, color: e.target.value })}
                    className="h-6 w-10 rounded cursor-pointer" data-testid="ft-bg-color" />
                </div>
                <div>
                  <span className="text-[10px] text-slate-500">Opacity: {Math.round((bgHighlight.opacity ?? 0.6) * 100)}%</span>
                  <Slider value={[(bgHighlight.opacity ?? 0.6) * 100]} onValueChange={([val]) => u('bgHighlight', { ...bgHighlight, opacity: val / 100 })} max={100} step={5} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[10px] text-slate-500">Padding: {bgHighlight.padding || 12}px</span>
                    <Slider value={[bgHighlight.padding || 12]} onValueChange={([val]) => u('bgHighlight', { ...bgHighlight, padding: val })} max={40} step={2} />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500">Radius: {bgHighlight.borderRadius || 0}px</span>
                    <Slider value={[bgHighlight.borderRadius || 0]} onValueChange={([val]) => u('bgHighlight', { ...bgHighlight, borderRadius: val })} max={24} step={2} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default CanvaTextToolbar;
